
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import { analyzeSavings } from 'app/calcs/RetirementSavingsAnalyzer'
import { ageAtAsDuration, dateAtAge, deadBy, IRetirementPerson, isValidPerson, Profile, retirementDate } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import * as _ from 'lodash'
import * as moment from 'moment'
import { groupByFractionalYears } from '../Accountant/Transactions/predicates';

type TSavingsFixedBalances = 'retirementSavings' | 'fourxxBalance' | 'iraBalance' | 'personalSavings' | 'healthReimbursementAccount'
interface ISavingsFixedBalances {
  retirementSavings: number
  fourxxBalance: number
  iraBalance: number
  personalSavings: number
  healthReimbursementAccount: number
}

interface IRatesOfReturn {
  default: number
}

interface IRetirementSavingsPersonInputs {
  // information describing incoming amounts
  fixedBalances: utils.HashedNumbers<TSavingsFixedBalances>,
  annualContributions: {
    amount: number,
    stopWhen: moment.Duration
  },

  // information describing withdrawl of retirement savings
  withdrawals: {
    monthlyAmount: number,
    startWhen: moment.Duration
  }
}

const defaultRatesOfReturn: IRatesOfReturn = {
  default: 6
}

const defaultRetirementSavingsPersonInputs: IRetirementSavingsPersonInputs = {
  fixedBalances: {},
  annualContributions: {
    amount: 0,
    stopWhen: null
  },
  withdrawals: {
    monthlyAmount: 0,
    startWhen: null
  }
}

const retirementSavingsSource = 'Retirement Savings'

const defaultSwitchover = moment.duration(65, 'years')

function ensureFixedBalances(inputs: IRetirementSavingsPersonInputs): IRetirementSavingsPersonInputs {
  inputs.fixedBalances = inputs.fixedBalances || {}
  return inputs
}

function ensureAnnualContributions(inputs: IRetirementSavingsPersonInputs): IRetirementSavingsPersonInputs {

  inputs.annualContributions = inputs.annualContributions || {
    amount: 0,
    stopWhen: defaultSwitchover.clone()
  }
  return inputs
}

function ensureWithdrawals(inputs: IRetirementSavingsPersonInputs): IRetirementSavingsPersonInputs {
  inputs.withdrawals = inputs.withdrawals || {
    monthlyAmount: 0,
    startWhen: defaultSwitchover.clone()
  }
  return inputs
}

function ensureRetirementSavingsPersonInputs(inputs: IRetirementSavingsPersonInputs): IRetirementSavingsPersonInputs {
  inputs = inputs || {
    fixedBalances: undefined,
    withdrawals: undefined,
    annualContributions: undefined
  }

  ensureFixedBalances(inputs)
  ensureAnnualContributions(inputs)
  ensureWithdrawals(inputs)

  // tslint:disable-next-line:no-console
  //console.log('ensure output: ' + JSON.stringify(inputs, undefined, 4)

  return inputs
}

function getDefaultRetirementSavingsPersonInputs() {
  return _.cloneDeep(defaultRetirementSavingsPersonInputs)
}

interface IRetirementSavingsOutputs {
  totalFixedBalances: number,
  accumulatedNewMonies: number,
  elapsedDuration: moment.Duration,
  savings: number,
  transactions: TX.ITransactionItem[]
}

interface IRetirementWithdrawals {
  fullWithdrawalDuration: moment.Duration,
  fullMonthWithdrawalAmount: number,
  lastWithdrawalDate: moment.Moment,
  partialWithdrawlMonthAmount: number,
  effectiveWithdrawalRate: number,
  savings: IRetirementSavingsOutputs,
  transactions: TX.ITransactionItem[]
}

class RetirementSavingsCalculations implements TX.ITransactionGenerator {

  analyzer = Promise.resolve(undefined)

  constructor(protected profile: Profile) {

    const member = profile.getHeadOfHousehold()
    const memberInputs = profile.getRetirementForHeadOfHouseHold()
    const spouse = profile.getSpouse()
    const spouseInputs = profile.getRetirementForSpouse()
    const req = {
      member, memberInputs,
      spouse, spouseInputs,
      ratesOfReturn: profile.ratesOfReturn
    }
    this.analyzer = analyzeSavings(req)
  }

  getPersonTimelineTransactions(person: IRetirementPerson): Promise<TX.ITransactionItem[]> {
    const r: TX.ITransactionItem[] = []
    if (!isValidPerson(person)) { return Promise.resolve(r) }

    // tslint:disable-next-line:no-console
    //console.log('getPersonTimelineTransactions entered for: ' + person.firstName)
    return this.analyzer
      .then((analyses) => {
        if (!analyses) {
          // tslint:disable-next-line:no-debugger
          //debugger
          return r
        }
        const analysis = (person === this.profile.getHeadOfHousehold()) ? analyses.member : analyses.spouse
        const firstWithdrawal = analysis.firstWithdrawalInfo
        if (firstWithdrawal) {
          const age1 = ageAtAsDuration(person, firstWithdrawal.when)
          r.push({
            when: firstWithdrawal.when,
            section: 'Retirement Savings',
            source: retirementSavingsSource,
            who: person.firstName,
            what: {
              retirementWithdrawal: firstWithdrawal.allowedWithdrawal
            },
            description: person.firstName + ' begins retirement savings withdrawals at age ' + utils.toNiceAge(age1, { showMonths: false }) + ' of <a>' + utils.toCurrency(firstWithdrawal.allowedWithdrawal) + '</a>'
          })
        }

        const lastWithdrawal = analysis.lastWithdrawalInfo
        if (lastWithdrawal) {
          const age2 = ageAtAsDuration(person, lastWithdrawal.when)
          r.push({
            when: lastWithdrawal.when,
            section: 'Retirement Savings',
            source: retirementSavingsSource,
            who: person.firstName,
            what: {
              finalRetirementWithdrawal: lastWithdrawal.allowedWithdrawal
            },
            description: utils.personPossessive(person.firstName)
              + ' final retirement savings withdrawal at age '
              + utils.toNiceAge(age2, { showMonths: false })
              + ' of <a>'
              + utils.toCurrency(lastWithdrawal.allowedWithdrawal)
              + '</a> leaving a balance of <a>'
              + utils.toCurrency(analysis.finalBalance)
              + '</a>'
          })
        }

        return r
      })
  }

  getTimeLineTransactions(): Promise<TX.ITransactionItem[]> {
    // tslint:disable-next-line:array-type
    const tasks: Promise<TX.ITransactionItem[]>[] = []

    const member = this.profile.getHeadOfHousehold()
    tasks.push(this.getPersonTimelineTransactions(member))

    const spouse = this.profile.getSpouse()
    tasks.push(this.getPersonTimelineTransactions(spouse))

    return Promise.all(tasks).then((x) => _.flatten(x))
  }

  getTransactions(when: moment.Moment, tolerance: TX.TransactionTolerance = 'month'): Promise<TX.ITransactionItem[]> {

    const results: TX.ITransactionItem[] = []

    const tasks = _.map(this.profile.retirement, (inputs, personsId) => {

      const person = this.profile.getMember(personsId)
      if (!person) { return } // if missing person...
      if (!isValidPerson(person)) { return }
      if (!DateCalcs.isAlive(person, when)) { return }
      if (!inputs) { return }

      const member = this.profile.getHeadOfHousehold()
      const isMember = member === person
      return this.analyzer
        .then((analysis) => {
          if (!analysis) { return results }
          const st2 = moment.utc()
          const section = isMember ? analysis.member : analysis.spouse
          const info = section.getBalances(when)
          if (info && info.allowedWithdrawal) {
            results.push({
              when,
              section: 'Retirement Savings',
              source: retirementSavingsSource,
              who: person.firstName,
              what: {
                retirementSavings: info.allowedWithdrawal
              },
              description: 'Retirement savings income for ' + person.firstName
            })
          }

          const e1 = moment.utc().diff(st2)
          // tslint:disable-next-line:no-console
          //console.log('analyzeSavings ' + seq + ' inner code took ' + e1)
          return results
        })
    })

    // tslint:disable-next-line:no-console
    //console.info('retirement transactions ' + JSON.stringify(results, undefined, 4))
    //printTransactions(results)

    return Promise.all(tasks)
      .then(() => results)
  }
}

export {
  ensureRetirementSavingsPersonInputs,
  ensureFixedBalances,
  ensureAnnualContributions,
  ensureWithdrawals,
  getDefaultRetirementSavingsPersonInputs,
  ISavingsFixedBalances,
  TSavingsFixedBalances,
  IRetirementSavingsPersonInputs,
  RetirementSavingsCalculations,
  IRetirementSavingsOutputs,
  IRetirementWithdrawals,
  IRatesOfReturn,
  defaultRatesOfReturn
}



// WEBPACK FOOTER //
// ./src/app/calcs/retirementSavings/index.ts